import { useEffect, useState } from "react";

import { useDeleteInstrument } from "../../api/hooks/useDeleteInstrument";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

import type { InstrumentResponse } from "./InstrumentFormDialog";

export type DeleteInstrumentDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instrument: InstrumentResponse | null;
};

export function DeleteInstrumentDialog({
  open,
  onOpenChange,
  instrument,
}: DeleteInstrumentDialogProps) {
  const deleteMutation = useDeleteInstrument();
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSubmitError(null);
    }
  }, [open]);

  async function onConfirm() {
    if (instrument === null) {
      return;
    }
    setSubmitError(null);
    try {
      await deleteMutation.mutateAsync({
        instrumentId: instrument.instrument_id,
      });
      onOpenChange(false);
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : "Failed to delete instrument. Try again.",
      );
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this instrument?</AlertDialogTitle>
          <AlertDialogDescription>
            {instrument === null
              ? null
              : `${instrument.symbol} will be hidden from the active list. You can restore it from the Show deleted view.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {submitError !== null && (
          <p role="alert" className="text-sm font-medium text-error">
            {submitError}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteMutation.isPending}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            className="bg-danger text-on-danger hover:opacity-90"
            disabled={deleteMutation.isPending || instrument === null}
            onClick={(event) => {
              event.preventDefault();
              void onConfirm();
            }}
          >
            {deleteMutation.isPending ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
